import React, { useEffect, useRef, useState } from "react";
import { connect, useSelector } from "react-redux";
import { toastify, toastType } from "util/toastify";

import { Avatar, CircularProgress, Stack } from "@mui/material";

import { setAvatar } from "../../../actions/authAction";
import { serviceUploadPhoto, serviceStore } from "../../../actions/serviceAction";
import { uploadFile } from "../../../util/s3";

import { useAtom } from "jotai";
import { serviceRegistration } from "../../../store/jotai";
import trade_favicon_transparent_bg from "../../../assets/images/trade_favicon_transparent_bg.png";

const ServiceStep4Page = ({ serviceUploadPhoto, setAvatar, serviceStore, onValidChange }) => {
  const service = useSelector((state) => state.service);

  const hiddenFileInput = useRef(null);

  const [photoName, setPhotoName] = useState("");
  const [showSpinner, setShowSpinner] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);

  const [emailEnabled, setEmailEnabled] = useState(true);
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState("");

  const [regTemp, setRegTemp] = useAtom(serviceRegistration);
  const [regTempStatus, setRegTempStatus] = useState(null);

  // sync regTemp to local display state only — no Redux store call here
  useEffect(() => {
    setRegTempStatus(regTemp);
  }, [regTemp]);

  // upload photo to backend once we have the name
  useEffect(() => {
    if (photoName !== "") {
      serviceUploadPhoto(photoName);
      setAvatar(photoName);
    }
  }, [photoName, serviceUploadPhoto, setAvatar]);

  const smsBlocked = smsEnabled && phoneNumber.trim() === "";
  const uploadBlocked = showSpinner && uploadPercent < 100;
  const joinDisabled = smsBlocked || uploadBlocked;

  useEffect(() => {
    if (onValidChange) onValidChange(joinDisabled);
  }, [joinDisabled, onValidChange]);

  // store notification prefs directly when user changes them
  const handleEmailChange = (checked) => {
    setEmailEnabled(checked);
    serviceStore({
      num: 6,
      yourSelf: service.serviceData["servicePresentation"] || "",
      notificationEnabled: checked,
      smsEnabled,
      phoneNumber,
      flag: false,
    });
  };

  const handleSmsChange = (checked) => {
    setSmsEnabled(checked);
    serviceStore({
      num: 6,
      yourSelf: service.serviceData["servicePresentation"] || "",
      notificationEnabled: emailEnabled,
      smsEnabled: checked,
      phoneNumber,
      flag: false,
    });
  };

  const handlePhoneChange = (val) => {
    const digits = val.replace(/\D/g, "");
    setPhoneNumber(digits);
    serviceStore({
      num: 6,
      yourSelf: service.serviceData["servicePresentation"] || "",
      notificationEnabled: emailEnabled,
      smsEnabled,
      phoneNumber: digits,
      flag: false,
    });
  };

  const handleFileChange = async (e) => {
    const fileUpload = e.target.files[0];
    if (!fileUpload?.type?.startsWith("image/")) {
      toastify("Please upload a valid image file", toastType.error);
      return;
    }
    setShowSpinner(true);
    await uploadFile(fileUpload, setPhotoName, setUploadPercent);
    setRegTemp({
      ...regTemp,
      avatar: URL.createObjectURL(fileUpload),
    });
  };

  return (
    <div>
      {/* Photo Upload — same as before, untouched */}
      <p className="service-text-service">Upload a photo</p>
      <p className="service-text mt-6 mb-16 leading-none">
        Upload your photo in which your face is clear it
        <br />
        helps you get more clients.
      </p>

      <div className="mb-12">
        <div className="service-avatar">
          <div className="service-photo">
            <Stack
              direction="row relative"
              spacing={2}
              className="justify-center z-10"
            >
              <Avatar
                alt="Uploaded Avatar"
                src={regTempStatus ? regTempStatus.avatar : (trade_favicon_transparent_bg || "")}
                sx={{ width: 150, height: 150 }}
              />
              {showSpinner && uploadPercent < 100 && (
                <div className="absolute flex justify-center items-center w-full h-full z-10 top-0 left-0 bg-slate-400 opacity-50 rounded-full">
                  <CircularProgress className="z-20" />
                </div>
              )}
              {showSpinner && uploadPercent < 100 && (
                <div className="absolute flex justify-center items-center w-full h-full z-10 top-0 left-0 rounded-full">
                  <span className="text-white">{uploadPercent}%</span>
                </div>
              )}
            </Stack>
            <button
              className="absolute bottom-0 right-0 w-10 h-10 rounded-full bg-main border-main text-white hover:bg-second hover:border-second transition-all z-10"
              onClick={() => hiddenFileInput.current.click()}
            >
              <i className="fa fa-upload"></i>
            </button>
          </div>
        </div>
      </div>

      <input
        type="file"
        ref={hiddenFileInput}
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      {/* Notifications */}
      <div className="mt-4 mb-4">
        <p className="service-text-label font-semibold mb-3">
          How would you like to receive notifications when someone requests a service from you on{" "}
          <span className="text-[#1cc400]">TRADE</span>IT<span className="text-[#1cc400]">FWD</span>
          <sup className="text-[#1cc400]" style={{ fontSize: "9px", top: "-0.8em" }}>TM</sup>?
        </p>

        <div className="flex items-center mb-3">
          <input
            type="checkbox"
            id="email-notify"
            checked={emailEnabled}
            onChange={(e) => handleEmailChange(e.target.checked)}
            className="w-4 h-4 mr-2 cursor-pointer"
          />
          <label htmlFor="email-notify" className="text-sm font-semibold cursor-pointer select-none">
            EMAIL
          </label>
        </div>

        <div className="flex items-center mb-2">
          <input
            type="checkbox"
            id="sms-notify"
            checked={smsEnabled}
            onChange={(e) => handleSmsChange(e.target.checked)}
            className="w-4 h-4 mr-2 cursor-pointer"
          />
          <label htmlFor="sms-notify" className="text-sm font-semibold cursor-pointer select-none">
            SMS
          </label>
        </div>

        {smsEnabled && (
          <div className="mt-2 ml-6">
            <input
              type="tel"
              inputMode="numeric"
              placeholder="Phone Number"
              value={phoneNumber}
              onChange={(e) => handlePhoneChange(e.target.value)}
              className="form-control !py-2.5 rounded-xl"
            />
            {smsBlocked && (
              <p className="text-red-500 text-xs mt-1">
                Please enter a phone number to enable SMS notifications.
              </p>
            )}
          </div>
        )}
      </div>

    </div>
  );
};

const mapStateToProps = (state) => ({
  service: state.service,
});
export default connect(mapStateToProps, { serviceUploadPhoto, setAvatar, serviceStore })(ServiceStep4Page);
